import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils.js';

/**
 * shadcn dialog — centered modal used for confirmation flows and
 * quick-action surfaces (the Slice 6 PR#3 deletion confirmation
 * dialog uses this primitive, not Sheet, because the action is
 * centered-on-screen rather than side-anchored).
 *
 * Center is the default side. The `Sheet` primitive is the slide-
 * over variant — same Radix underpinnings, different animation.
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

export interface DialogContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    'onPointerDownOutside' | 'onFocusOutside' | 'onEscapeKeyDown'
  > {
  /**
   * Cerrar al tocar/hacer clic FUERA del modal (overlay) o cuando el
   * foco se va del diálogo.
   *
   * Default `false`: casi todos los modales del producto son formularios
   * de carga, y un toque accidental al costado descartaba todo lo
   * escrito sin aviso (reporte del cliente 2026-10-27: "alta
   * sensibilidad al tacto... se cierran y se pierden los cambios no
   * guardados"). El modal se cierra únicamente con la X de la esquina
   * superior derecha o con el botón Cancelar del footer.
   *
   * Un modal de confirmación / lectura rápida puede pedir el
   * comportamiento nativo pasando `closeOnOutsideClick`.
   */
  closeOnOutsideClick?: boolean;
  /**
   * Cerrar con la tecla Escape. Default `false` por la misma razón: el
   * cliente pidió que la única salida sea el botón de cierre. Pasar
   * `closeOnEscape` en superficies de lectura donde Escape es esperable.
   */
  closeOnEscape?: boolean;
  onPointerDownOutside?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >['onPointerDownOutside'];
  onFocusOutside?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>['onFocusOutside'];
  onEscapeKeyDown?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >['onEscapeKeyDown'];
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      style,
      closeOnOutsideClick = false,
      closeOnEscape = false,
      onPointerDownOutside,
      onFocusOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
          className,
        )}
        // Inline fallback: keep the modal centered even when the Tailwind
        // translate utilities are missing from the dev CSS bundle (stale
        // Vite cache / purged preflight). Uses the independent `translate`
        // property (same as Tailwind v4) so it never fights the
        // zoom-in/out `scale` animation. The utility classes stay as the
        // primary path; this only guarantees the same result.
        style={{ top: '50%', left: '50%', translate: '-50% -50%', ...style } as React.CSSProperties}
        onPointerDownOutside={(event) => {
          if (!closeOnOutsideClick) event.preventDefault();
          onPointerDownOutside?.(event);
        }}
        onFocusOutside={(event) => {
          // Radix also treats a focus jump (on-screen keyboard, another
          // control) as an outside interaction. Same opt-in rule.
          if (!closeOnOutsideClick) event.preventDefault();
          onFocusOutside?.(event);
        }}
        onEscapeKeyDown={(event) => {
          if (!closeOnEscape) event.preventDefault();
          onEscapeKeyDown?.(event);
        }}
        {...props}
      >
        {children}
        {/* Close rail: pinned to the top of the VISIBLE area so the X stays
            reachable while the modal body scrolls (several modals scroll at
            the Content level via `max-h-[90vh] overflow-y-auto`).
            Geometry is INLINE on purpose: Tailwind does not scan
            `packages/ui`, so utilities authored only here (w-11, h-0,
            order-first, -mb-4…) never reach the stylesheet. The rail is
            zero-height and `order: -1` so it takes no space and sits first
            visually while staying LAST in the DOM — Radix autofocus must
            keep landing on the first field, not on the close button.
            The touch target is 44x44 (icon stays 16px): a 16px hit area was
            part of the "alta sensibilidad al tacto" complaint. */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            order: -1,
            height: 0,
            marginBottom: -16,
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            pointerEvents: 'none',
          }}
        >
          <DialogPrimitive.Close
            aria-label="Cerrar ventana"
            style={{
              pointerEvents: 'auto',
              marginTop: -14,
              marginRight: -14,
              width: 44,
              height: 44,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="rounded-md opacity-70 transition-colors hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </DialogPrimitive.Close>
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = 'DialogContent';

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactElement => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
